"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
exports.getPixelIndexArray = getPixelIndexArray;

var _BuilderType = _interopRequireDefault(require("./BuilderType.js"));

var _Executor = _interopRequireDefault(require("./Executor.js"));

var _extent = require("../../extent.js");

var _transform = require("../../transform.js");

var _dom = require("../../dom.js");

var _obj = require("../../obj.js");

var _array = require("../../array.js");

var _transform2 = require("../../geom/flat/transform.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/render/canvas/ExecutorGroup
 */

/**
 * @const
 * @type {Array<import("./BuilderType.js").default>}
 */
var ORDER = [_BuilderType["default"].POLYGON, _BuilderType["default"].CIRCLE, _BuilderType["default"].LINE_STRING, _BuilderType["default"].IMAGE, _BuilderType["default"].TEXT, _BuilderType["default"].DEFAULT];

var ExecutorGroup =
/** @class */
function () {
  /**
   * @param {import("../../extent.js").Extent} maxExtent Max extent for clipping. When a
   * `maxExtent` was set on the Builder for this executor group, the same `maxExtent`
   * should be set here, unless the target context does not exceed that extent (which
   * can be the case when rendering to tiles).
   * @param {number} resolution Resolution.
   * @param {number} pixelRatio Pixel ratio.
   * @param {boolean} overlaps The executor group can have overlapping geometries.
   * @param {!Object<string, !Object<import("./BuilderType.js").default, import("../canvas.js").SerializableInstructions>>} allInstructions
   * The serializable instructions.
   * @param {number} [opt_renderBuffer] Optional rendering buffer.
   */
  function ExecutorGroup(maxExtent, resolution, pixelRatio, overlaps, allInstructions, opt_renderBuffer) {
    /**
     * @private
     * @type {import("../../extent.js").Extent}
     */
    this.maxExtent_ = maxExtent;
    /**
     * @private
     * @type {boolean}
     */

    this.overlaps_ = overlaps;
    /**
     * @private
     * @type {number}
     */

    this.pixelRatio_ = pixelRatio;
    /**
     * @private
     * @type {number}
     */

    this.resolution_ = resolution;
    /**
     * @private
     * @type {number|undefined}
     */

    this.renderBuffer_ = opt_renderBuffer;
    /**
     * @private
     * @type {!Object<string, !Object<import("./BuilderType.js").default, import("./Executor").default>>}
     */

    this.executorsByZIndex_ = {};
    /**
     * @private
     * @type {CanvasRenderingContext2D}
     */

    this.hitDetectionContext_ = null;
    /**
     * @private
     * @type {import("../../transform.js").Transform}
     */

    this.hitDetectionTransform_ = (0, _transform.create)();
    this.createExecutors_(allInstructions);
  }
  /**
   * @param {CanvasRenderingContext2D} context Context.
   * @param {import("../../transform.js").Transform} transform Transform.
   */


  ExecutorGroup.prototype.clip = function (context, transform) {
    var flatClipCoords = this.getClipCoords(transform);
    context.beginPath();
    context.moveTo(flatClipCoords[0], flatClipCoords[1]);
    context.lineTo(flatClipCoords[2], flatClipCoords[3]);
    context.lineTo(flatClipCoords[4], flatClipCoords[5]);
    context.lineTo(flatClipCoords[6], flatClipCoords[7]);
    context.clip();
  };
  /**
   * Create executors and populate them using the provided instructions.
   * @private
   * @param {!Object<string, !Object<import("./BuilderType.js").default, import("../canvas.js").SerializableInstructions>>} allInstructions The serializable instructions
   */


  ExecutorGroup.prototype.createExecutors_ = function (allInstructions) {
    for (var zIndex in allInstructions) {
      var executors = this.executorsByZIndex_[zIndex];

      if (executors === undefined) {
        executors = {};
        this.executorsByZIndex_[zIndex] = executors;
      }

      var instructionByZindex = allInstructions[zIndex];

      for (var builderType in instructionByZindex) {
        var instructions = instructionByZindex[builderType];
        executors[builderType] = new _Executor["default"](this.resolution_, this.pixelRatio_, this.overlaps_, instructions);
      }
    }
  };
  /**
   * @param {Array<import("./BuilderType.js").default>} executors Executors.
   * @return {boolean} Has executors of the provided types.
   */


  ExecutorGroup.prototype.hasExecutors = function (executors) {
    for (var zIndex in this.executorsByZIndex_) {
      var candidates = this.executorsByZIndex_[zIndex];

      for (var i = 0, ii = executors.length; i < ii; ++i) {
        if (executors[i] in candidates) {
          return true;
        }
      }
    }

    return false;
  };
  /**
   * @param {import("../../coordinate.js").Coordinate} coordinate Coordinate.
   * @param {number} resolution Resolution.
   * @param {number} rotation Rotation.
   * @param {number} hitTolerance Hit tolerance in pixels.
   * @param {function(import("../../Feature.js").FeatureLike, import("../../geom/SimpleGeometry.js").default, number): T} callback Feature callback.
   * @param {Array<import("../../Feature.js").FeatureLike>} declutteredFeatures Decluttered features.
   * @return {T|undefined} Callback result.
   * @template T
   */


  ExecutorGroup.prototype.forEachFeatureAtCoordinate = function (coordinate, resolution, rotation, hitTolerance, callback, declutteredFeatures) {
    hitTolerance = Math.round(hitTolerance);
    var contextSize = hitTolerance * 2 + 1;
    var transform = (0, _transform.compose)(this.hitDetectionTransform_, hitTolerance + 0.5, hitTolerance + 0.5, 1 / resolution, -1 / resolution, -rotation, -coordinate[0], -coordinate[1]);
    var newContext = !this.hitDetectionContext_;

    if (newContext) {
      this.hitDetectionContext_ = (0, _dom.createCanvasContext2D)(contextSize, contextSize);
    }

    var context = this.hitDetectionContext_;

    if (context.canvas.width !== contextSize || context.canvas.height !== contextSize) {
      context.canvas.width = contextSize;
      context.canvas.height = contextSize;
    } else if (!newContext) {
      context.clearRect(0, 0, contextSize, contextSize);
    }
    /**
     * @type {import("../../extent.js").Extent}
     */


    var hitExtent;

    if (this.renderBuffer_ !== undefined) {
      hitExtent = (0, _extent.createEmpty)();
      (0, _extent.extendCoordinate)(hitExtent, coordinate);
      (0, _extent.buffer)(hitExtent, resolution * (this.renderBuffer_ + hitTolerance), hitExtent);
    }

    var indexes = getPixelIndexArray(hitTolerance);
    var builderType;
    /**
     * @param {import("../../Feature.js").FeatureLike} feature Feature.
     * @param {import("../../geom/SimpleGeometry.js").default} geometry Geometry.
     * @return {T|undefined} Callback result.
     */

    function featureCallback(feature, geometry) {
      var imageData = context.getImageData(0, 0, contextSize, contextSize).data;

      for (var i_1 = 0, ii = indexes.length; i_1 < ii; i_1++) {
        if (imageData[indexes[i_1]] > 0) {
          if (!declutteredFeatures || builderType !== _BuilderType["default"].IMAGE && builderType !== _BuilderType["default"].TEXT || declutteredFeatures.indexOf(feature) !== -1) {
            var idx = (indexes[i_1] - 3) / 4;
            var x = hitTolerance - idx % contextSize;
            var y = hitTolerance - (idx / contextSize | 0);
            var result_1 = callback(feature, geometry, x * x + y * y);

            if (result_1) {
              return result_1;
            }
          }

          context.clearRect(0, 0, contextSize, contextSize);
          break;
        }
      }

      return undefined;
    }
    /** @type {Array<number>} */


    var zs = Object.keys(this.executorsByZIndex_).map(Number);
    zs.sort(_array.numberSafeCompareFunction);
    var i, j, executors, executor, result;

    for (i = zs.length - 1; i >= 0; --i) {
      var zIndexKey = zs[i].toString();
      executors = this.executorsByZIndex_[zIndexKey];

      for (j = ORDER.length - 1; j >= 0; --j) {
        builderType = ORDER[j];
        executor = executors[builderType];

        if (executor !== undefined) {
          result = executor.executeHitDetection(context, transform, rotation, featureCallback, hitExtent);

          if (result) {
            return result;
          }
        }
      }
    }

    return undefined;
  };
  /**
   * @param {import("../../transform.js").Transform} transform Transform.
   * @return {Array<number>} Clip coordinates.
   */


  ExecutorGroup.prototype.getClipCoords = function (transform) {
    var maxExtent = this.maxExtent_;

    if (!maxExtent) {
      return null;
    }

    var minX = maxExtent[0];
    var minY = maxExtent[1];
    var maxX = maxExtent[2];
    var maxY = maxExtent[3];
    var flatClipCoords = [minX, minY, minX, maxY, maxX, maxY, maxX, minY];
    (0, _transform2.transform2D)(flatClipCoords, 0, 8, 2, transform, flatClipCoords);
    return flatClipCoords;
  };
  /**
   * @return {boolean} Is empty.
   */


  ExecutorGroup.prototype.isEmpty = function () {
    return (0, _obj.isEmpty)(this.executorsByZIndex_);
  };
  /**
   * @param {CanvasRenderingContext2D} context Context.
   * @param {number} contextScale Scale of the context.
   * @param {import("../../transform.js").Transform} transform Transform.
   * @param {number} viewRotation View rotation.
   * @param {boolean} snapToPixel Snap point symbols and test to integer pixel.
   * @param {Array<import("./BuilderType.js").default>} [opt_builderTypes] Ordered replay types to replay.
   *     Default is {@link module:ol/render/replay~ORDER}
   * @param {import("rbush").default} [opt_declutterTree] Declutter tree.
   */


  ExecutorGroup.prototype.execute = function (context, contextScale, transform, viewRotation, snapToPixel, opt_builderTypes, opt_declutterTree) {
    /** @type {Array<number>} */
    var zs = Object.keys(this.executorsByZIndex_).map(Number);
    zs.sort(_array.numberSafeCompareFunction); // setup clipping so that the parts of over-simplified geometries are not
    // visible outside the current extent when panning

    if (this.maxExtent_) {
      context.save();
      this.clip(context, transform);
    }

    var builderTypes = opt_builderTypes ? opt_builderTypes : ORDER;
    var i, ii, j, jj, replays, replay;

    if (opt_declutterTree) {
      zs.reverse();
    }

    for (i = 0, ii = zs.length; i < ii; ++i) {
      var zIndexKey = zs[i].toString();
      replays = this.executorsByZIndex_[zIndexKey];

      for (j = 0, jj = builderTypes.length; j < jj; ++j) {
        var builderType = builderTypes[j];
        replay = replays[builderType];

        if (replay !== undefined) {
          replay.execute(context, contextScale, transform, viewRotation, snapToPixel, opt_declutterTree);
        }
      }
    }

    if (this.maxExtent_) {
      context.restore();
    }
  };

  return ExecutorGroup;
}();
/**
 * This cache is used to store arrays of indexes for calculated pixel circles
 * to increase performance.
 * It is a static property to allow each Replaygroup to access it.
 * @type {Object<number, Array<number>>}
 */


var circlePixelIndexArrayCache = {};
/**
 * This methods creates an array with indexes of all pixels within a circle,
 * ordered by how close they are to the center.
 * A cache is used to increase performance.
 * @param {number} radius Radius.
 * @return {Array<number>} An array with indexes within a circle.
 */

function getPixelIndexArray(radius) {
  if (circlePixelIndexArrayCache[radius] !== undefined) {
    return circlePixelIndexArrayCache[radius];
  }

  var size = radius * 2 + 1;
  var maxDistanceSq = radius * radius;
  var distances = new Array(maxDistanceSq + 1);

  for (var i = 0; i <= radius; ++i) {
    for (var j = 0; j <= radius; ++j) {
      var distanceSq = i * i + j * j;

      if (distanceSq > maxDistanceSq) {
        break;
      }

      var distance = distances[distanceSq];

      if (!distance) {
        distance = [];
        distances[distanceSq] = distance;
      }

      distance.push(((radius + i) * size + (radius + j)) * 4 + 3);

      if (i > 0) {
        distance.push(((radius - i) * size + (radius + j)) * 4 + 3);
      }

      if (j > 0) {
        distance.push(((radius + i) * size + (radius - j)) * 4 + 3);

        if (i > 0) {
          distance.push(((radius - i) * size + (radius - j)) * 4 + 3);
        }
      }
    }
  }

  var pixelIndex = [];

  for (var i = 0, ii = distances.length; i < ii; ++i) {
    if (distances[i]) {
      pixelIndex.push.apply(pixelIndex, distances[i]);
    }
  }

  circlePixelIndexArrayCache[radius] = pixelIndex;
  return pixelIndex;
}

var _default = ExecutorGroup;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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